import React, { useState } from 'react';
import { History, User, Trash2, Loader2, Plus, ArrowRight, CheckCircle2, XCircle } from 'lucide-react';
import { deleteProspectAction } from '../services/prospectService';
import { STATUT_MAP, formatDateTime, formatDate, isWaitingAction } from '../config/prospects';

/**
 * Page / Vue dédiée à l'Historique complet d'un prospect.
 */
const ProspectHistory = ({ prospect, onUpdated, onNewAction }) => {
    const [deletingId, setDeletingId] = useState(null);
    const [error, setError] = useState('');

    const handleDeleteEntry = async (entryId) => {
        setDeletingId(entryId);
        setError('');
        try {
            const updated = await deleteProspectAction(prospect._id, entryId);
            onUpdated?.(updated, "Entrée d'historique supprimée");
        } catch (err) {
            setError(err.response?.data?.message || 'Suppression impossible.');
        } finally {
            setDeletingId(null);
        }
    };

    const historique = [...(prospect.historique || [])].sort((a, b) => new Date(b.date) - new Date(a.date));

    return (
        <div className="space-y-6">
            {/* Situation recap */}
            <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Dernière action</div>
                    <div className="text-xs font-bold text-slate-900 truncate" title={prospect.derniereAction}>
                        {prospect.derniereAction || '—'}
                    </div>
                    <div className="text-[10px] font-mono text-slate-400 mt-0.5">
                        {formatDate(prospect.dateDerniereAction, true)}
                    </div>
                </div>
                {prospect.statut === 'gagne' ? (
                    <div className="p-3 bg-emerald-50/70 border border-emerald-200 rounded-lg">
                        <div className="text-[9px] font-black uppercase tracking-widest text-emerald-600 mb-1">Statut commercial</div>
                        <div className="text-xs font-bold text-emerald-800 flex items-center gap-1">
                            <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />
                            Client converti 🎉
                        </div>
                        <div className="text-[10px] font-medium text-emerald-600 mt-0.5">Dossier clos avec succès</div>
                    </div>
                ) : prospect.statut === 'pas_interesse' ? (
                    <div className="p-3 bg-slate-100/70 border border-slate-200 rounded-lg">
                        <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Statut commercial</div>
                        <div className="text-xs font-bold text-slate-700 flex items-center gap-1">
                            <XCircle size={13} className="text-slate-400 shrink-0" />
                            Sans suite (Refus)
                        </div>
                        <div className="text-[10px] font-medium text-slate-500 mt-0.5">Archivé · Aucune relance</div>
                    </div>
                ) : isWaitingAction(prospect.prochaineAction) ? (
                    <div className="p-3 bg-indigo-50/60 border border-indigo-200 rounded-lg">
                        <div className="text-[9px] font-black uppercase tracking-widest text-indigo-500 mb-1">En attente</div>
                        <div className="text-xs font-bold text-indigo-900 truncate" title={prospect.prochaineAction}>
                            ⏳ {prospect.prochaineAction}
                        </div>
                        <div className="text-[10px] font-medium text-indigo-600 mt-0.5">
                            {prospect.dateProchaineAction ? `Relance : ${formatDate(prospect.dateProchaineAction)}` : "En attente de retour du client"}
                        </div>
                    </div>
                ) : (
                    <div className="p-3 bg-blue-50/60 border border-blue-200 rounded-lg">
                        <div className="text-[9px] font-black uppercase tracking-widest text-blue-500 mb-1">Prochaine action</div>
                        <div className="text-xs font-bold text-slate-900 truncate" title={prospect.prochaineAction}>
                            {prospect.prochaineAction || '—'}
                        </div>
                        <div className="text-[10px] font-mono text-blue-500 mt-0.5">
                            {formatDate(prospect.dateProchaineAction, true)}
                            {prospect.heureProchaineAction ? ` à ${prospect.heureProchaineAction}` : ''}
                        </div>
                    </div>
                )}
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Statut & Intérêt</div>
                    <div className="text-xs font-bold text-slate-900 truncate">
                        {STATUT_MAP[prospect.statut]?.emoji} {STATUT_MAP[prospect.statut]?.label}
                    </div>
                    <div className="text-[10px] font-mono text-slate-400 mt-0.5">
                        Intérêt {prospect.interet || 0}%
                    </div>
                    {prospect.objection && (
                        <div className="mt-1 text-[10px] font-bold text-red-600 truncate" title={prospect.objection}>
                            🛑 {prospect.objection}
                        </div>
                    )}
                </div>
            </div>

            {/* Header + CTA Nouvelle action */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.15em] text-slate-900">
                    <History size={15} className="text-blue-600" />
                    Historique des actions
                    <span className="text-[10px] font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md font-mono normal-case tracking-normal">
                        {historique.length}
                    </span>
                </div>
                {onNewAction && (
                    <button
                        type="button"
                        onClick={onNewAction}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition shadow-sm"
                    >
                        <Plus size={13} />
                        Nouvelle action
                    </button>
                )}
            </div>

            {error && (
                <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-xs font-bold rounded-lg">
                    {error}
                </div>
            )}

            {/* Timeline */}
            {historique.length === 0 ? (
                <div className="py-12 px-4 text-center border-2 border-dashed border-slate-200 rounded-xl space-y-3">
                    <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto">
                        <History size={20} />
                    </div>
                    <div className="text-sm font-bold text-slate-800">Aucune action enregistrée</div>
                    <p className="text-xs text-slate-400 max-w-xs mx-auto">
                        Consignez les appels, emails, réunions ou démos pour suivre l'avancement avec ce prospect.
                    </p>
                    {onNewAction && (
                        <button
                            type="button"
                            onClick={onNewAction}
                            className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-800 transition"
                        >
                            <Plus size={13} />
                            Enregistrer une première action
                        </button>
                    )}
                </div>
            ) : (
                <ol className="relative border-l-2 border-slate-200 ml-3 space-y-5 pt-1">
                    {historique.map(h => {
                        const st = STATUT_MAP[h.statut];
                        return (
                            <li key={h._id} className="ml-6 group">
                                <span className={`absolute -left-[7px] mt-2 w-3 h-3 rounded-full ring-4 ring-white ${st?.dot || 'bg-blue-500'}`} />
                                <div className="p-4 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-colors shadow-xs">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 space-y-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-sm font-bold text-slate-900">{h.action}</span>
                                                {h.resultat && (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200">
                                                        {h.resultat}
                                                    </span>
                                                )}
                                                {h.objection && (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-50 px-2 py-0.5 rounded-md border border-red-200">
                                                        🛑 {h.objection}
                                                    </span>
                                                )}
                                                {h.responsable && (
                                                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200/60">
                                                        <User size={11} className="text-slate-400" />
                                                        {h.responsable}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 text-[10px] font-mono text-slate-400">
                                                <span>{formatDateTime(h.date)}</span>
                                                {st && <span className="text-slate-600 font-sans font-semibold">· {st.emoji} {st.label}</span>}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteEntry(h._id)}
                                            disabled={deletingId === h._id}
                                            title="Supprimer cette entrée"
                                            className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-md transition shrink-0"
                                        >
                                            {deletingId === h._id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                                        </button>
                                    </div>
                                    {h.note && (
                                        <div className="mt-2.5 pt-2 border-t border-slate-100 text-xs text-slate-600 whitespace-pre-line leading-relaxed bg-slate-50/60 p-2.5 rounded-lg">
                                            {h.note}
                                        </div>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ol>
            )}
        </div>
    );
};

export default ProspectHistory;
